import { createContext, useCallback, useContext, useEffect, useState } from "react";

const FocusContext = createContext({ focus: false, toggleFocus: () => {} });

const STORAGE_KEY = "cf_focus_mode";

function applyFocus(on) {
  const root = document.documentElement;
  if (on) root.classList.add("focus-mode");
  else root.classList.remove("focus-mode");
}

/**
 * Focus Mode — Chairman glance-and-go.
 * Adds `.focus-mode` to <html>; CSS uses this to enlarge hero numbers,
 * pare down chrome (sidebar collapsed hint, kicker labels, secondary tables).
 */
export function FocusProvider({ children }) {
  const [focus, setFocus] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  });

  useEffect(() => {
    applyFocus(focus);
    try { window.localStorage.setItem(STORAGE_KEY, focus ? "1" : "0"); } catch { /* ignore */ }
  }, [focus]);

  const toggleFocus = useCallback(() => setFocus((v) => !v), []);

  // Keyboard shortcut ⌘⇧F
  useEffect(() => {
    const onKey = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.shiftKey && (e.key === "f" || e.key === "F")) {
        e.preventDefault();
        toggleFocus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggleFocus]);

  return (
    <FocusContext.Provider value={{ focus, toggleFocus }}>
      {children}
    </FocusContext.Provider>
  );
}

export function useFocus() {
  return useContext(FocusContext);
}
